import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface PercentageInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value'
> {
  value: string;
  onChange: (value: string) => void;
  decimalPlaces?: number;
  selectAllOnFocus?: boolean;
  showSymbol?: boolean;
  ref?: React.Ref<HTMLInputElement>;
}

function parsePercentageInput(
  input: string,
  decimalPlaces: number
): string | null {
  if (input === '') return '';

  let clean = input.replace(',', '.').replace(/[^\d.]/g, '');

  if (clean.includes('.')) {
    const parts = clean.split('.');
    if (parts.length > 2) return null; // reject second decimal point
    if (parts[1] && parts[1].length > decimalPlaces) {
      parts[1] = parts[1].substring(0, decimalPlaces);
      clean = parts.join('.');
    }
  }

  const num = Number.parseFloat(clean);
  if (!Number.isNaN(num) && num > 100) clean = '100';

  return clean;
}

function PercentageInput({
  className,
  value,
  onChange,
  decimalPlaces = 2,
  selectAllOnFocus = true,
  showSymbol = true,
  ref,
  onFocus,
  onBlur,
  ...props
}: PercentageInputProps) {
  // While focused, the input owns its display so the parent's parseFloat
  // round-trip ("25." -> 25 -> "25") cannot clobber a trailing dot mid-typing.
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const displayValue = editingValue ?? value;

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setEditingValue(value);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setEditingValue(null);
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const clean = parsePercentageInput(e.target.value, decimalPlaces);
    if (clean === null) return;
    setEditingValue(clean);
    onChange(clean);
  };

  const inputElement = (
    <Input
      ref={ref}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={cn(className)}
      selectAllOnFocus={selectAllOnFocus}
      min="0"
      max="100"
      {...props}
    />
  );

  if (showSymbol) {
    return (
      <div className="flex items-center gap-2">
        {inputElement}
        <span className="text-sm text-muted-foreground">%</span>
      </div>
    );
  }

  return inputElement;
}

export { PercentageInput };
